import { ChangeDetectorRef, Component, HostBinding, Input, OnInit, ViewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import moment from 'moment-timezone';
import { starAnimations } from '@pay/animations';
import { CollectionProgress } from 'app/services/organization/graphs/collection-progress.model';
import { DomHelperService } from 'app/services/dom-helper.service';
import { OrganizationService } from 'app/services/organization/organization.service';
import { Organization } from 'app/models/entities/organization';
import { LegFiJwtService } from 'app/services/auth/legfi-jwt.service';
import { ActiveElement, Chart, ChartEvent, ChartOptions } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';

@UntilDestroy()
@Component({
    selector: 'app-collection-progress-graph',
    templateUrl: './collection-progress-graph.component.html',
    styleUrls: ['./collection-progress-graph.component.scss'],
    animations: starAnimations,
})
export class CollectionProgressGraphComponent implements OnInit
{
    @HostBinding('class.card') card = true;

    collectionSummary: CollectionProgress = null;
    graphYear = new Date().getFullYear();
    showCalendarYear = 0;
    permissions = {};

    collectionGraphLoading: boolean = true;
    hasData = false;
    drilldown = null;
    @Input() title = 'Collection Progress';
    @Input() onDashboard = false;

    @ViewChild(BaseChartDirective) chart: BaseChartDirective<'bar'> | undefined;

    chartOptions: ChartOptions = {
        responsive: true,
        onClick: (
                event: ChartEvent,
                elements: ActiveElement[],
                chart: Chart<'bar'>,
        ) => {
            if (elements[0] && this.drilldown === null && this.collectionSummary.data[elements[0].index].drilldownTotals.invoiced > 0) {
                this.drilldown = elements[0].index;
                this.buildChartData();
            }
        },
        maintainAspectRatio: false,
        interaction: {
            intersect: false,
            mode: 'index',
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                enabled: false,

                external: function (context) {
                    // Tooltip Element
                    let tooltipEl = document.getElementById('chartjs-tooltip');

                    // Create element on first render
                    if (!tooltipEl) {
                        tooltipEl = document.createElement('div');
                        tooltipEl.id = 'chartjs-tooltip';
                        document.body.appendChild(tooltipEl);
                    }

                    // Hide if no tooltip
                    const tooltipModel = context.tooltip;
                    if (tooltipModel.opacity === 0) {
                        tooltipEl.style.opacity = '0';
                        return;
                    }

                    // Set caret Position
                    tooltipEl.classList.remove('above', 'below', 'no-transform');
                    if (tooltipModel.yAlign) {
                        tooltipEl.classList.add(tooltipModel.yAlign);
                    } else {
                        tooltipEl.classList.add('no-transform');
                    }

                    const titleLines = tooltipModel.title || [];

                    let innerHtml = '<table style="background-color: transparent;"><thead>';

                    titleLines.forEach(function (title) {
                        innerHtml += '<tr><th colspan="3" style="color: #d5dae0;">' + title + '</th></tr>';
                    });
                    innerHtml += '</thead><tbody>';

                    const formatter = new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                    });

                    const total = tooltipModel.dataPoints.map(d => d.parsed.y).reduce((a, b) => a + b, 0);
                    const formatted = formatter.format(total);
                    innerHtml += `<tr>
<td class="pt-3" colspan="2" style="text-align: left; padding-right: 10px; line-height: 1.2; color: #fff; font-family: \'ProximaNovaRegular\', arial, sans-serif">Total Invoiced:</td>
<td class="pt-3" style="text-align: right; margin-left: 10px; font-family: \'ProximaNovaSemibold\', arial, sans-serif; color: #FFFFFF;">${formatted}</td>
</tr>`;


                    tooltipModel.dataPoints.forEach((datapoint, i) => {
                        const colors = tooltipModel.labelColors[i];
                        const point = formatter.format(datapoint.parsed.y);
                        innerHtml += `<tr>
                                <td class="pt-2"><div style="background-color: ${colors.backgroundColor}; width: 12px; height: 12px; margin-right: 10px; border-radius: 50%;" ></div></td>
                                <td class="pt-2" style="text-align: left; padding-right: 10px; line-height: 1.2; color: #fff; font-family: \'ProximaNovaRegular\', arial, sans-serif">${datapoint.dataset.label}:</td>
                                <td class="pt-2" style="text-align: right; margin-left: 10px; font-family: \'ProximaNovaSemibold\', arial, sans-serif; color: #FFFFFF;">${point}</td>
                            </tr>`;
                    });

                    innerHtml += '</tbody></table>';

                    tooltipEl.innerHTML = innerHtml;

                    const position = context.chart.canvas.getBoundingClientRect();

                    // Display, position, and set styles for font
                    tooltipEl.style.opacity = '1';
                    tooltipEl.style.position = 'absolute';
                    tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + 'px';
                    tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
                    tooltipEl.style.font = 'ProximaNovaRegular';
                    tooltipEl.style.padding = '5px 10px 16px 10px';
                    tooltipEl.style.pointerEvents = 'none';
                    tooltipEl.style.backgroundColor = '#5B697D';
                },
            },
        },
        scales: {
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    color: '#383B46',
                    font: {
                        family: '\'Arial\'',
                        weight: '400',
                    },
                },
                stacked: true,
            },
            y: {
                grid: {
                    drawBorder: false,
                    drawTicks: false,
                    borderDash: [
                        5,
                        5,
                    ],
                },
                ticks: {
                    maxTicksLimit: 6,
                    color: '#383B46',
                    font: {
                        family: '\'Arial\'',
                        weight: '400',
                    },
                    callback: function (value: number) {
                        return '$' + value.toLocaleString();
                    },
                },
                stacked: true,
            },
        },
    };
    chartData = {
        labels: [],
        datasets: [
            {
                label: 'Collected',
                data: [],
                stack: 0,
                backgroundColor: '#44B99F',
                hoverBackgroundColor: '#49C4A9',
                barThickness: 17,
            },
            {
                label: 'Outstanding',
                data: [],
                stack: 0,
                backgroundColor: '#E4EBF3',
                hoverBackgroundColor: '#f8f9fa',
                barThickness: 17,
            },
        ],
    };

    yearFilter: number = new Date().getFullYear();
    yearFilters: number[] = [];
    @Input()
    public enableDrilldown: boolean = false;
    public legendInvoiceTotal = 0;
    public legendCollectedTotal = 0;
    public showRangePicker = true;
    hasWriteAccess = false;

    constructor(
            private _domHelper: DomHelperService,
            private _organizationService: OrganizationService,
            private _cd: ChangeDetectorRef,
    ) {
        this.determinePermissions();
    }

    ngOnInit() {
        const yearFilters: number[] = [];
        for (let i: number = new Date().getFullYear() + 1; i >= 2008; i--) {
            yearFilters.push(i);
        }
        this.yearFilters = yearFilters;

        this._organizationService
                .getOrganizationById(null, ['profile'])
                .pipe(untilDestroyed(this))
                .subscribe((org: Organization) => {
                    const fiscalStartMonth = org.profile.fiscalPeriodStart;

                    // Set the charts to next year if our start month has passed
                    if (fiscalStartMonth > 1 && new Date().getMonth() + 1 >= fiscalStartMonth) {
                        this.graphYear = this.graphYear + 1;
                    }
                    this.getCollectionProgressGraph();
                });
    }

    getCollectionProgressGraph(options: any = {}): void {
        this.collectionGraphLoading = true;

        options.year = this.graphYear;
        options.showCalendarYear = this.showCalendarYear;
        options.cats = this.enableDrilldown;

        this._organizationService
                .getCollectionProgress(null, options)
                .pipe(untilDestroyed(this))
                .subscribe(
                        (response: any) => {
                            this.collectionSummary = <CollectionProgress>response;
                            this.buildChartData();

                            this.collectionGraphLoading = false;
                            this.hasData = this.collectionSummary.data.length > 0;
                        },
                        () => {
                            alert(
                                    'There was an error retrieving the collection progress by month graph summary',
                            );
                        },
                );
    }

    buildChartData() {
        const drillDown = this.drilldown;
        if (drillDown !== null) {
            this.chartData.labels = this.collectionSummary.data[drillDown].drilldown.map(row => row.name);

            this.chartData.datasets[0].data = this.collectionSummary.data[drillDown].drilldown.map(row => row.collected / 100);
            this.chartData.datasets[1].data = this.collectionSummary.data[drillDown].drilldown.map(row => (row.invoiced - row.collected) / 100);

            this.legendInvoiceTotal = this.collectionSummary.data[drillDown].drilldownTotals.invoiced;
            this.legendCollectedTotal = this.collectionSummary.data[drillDown].drilldownTotals.collected;
        } else {
            this.chartData.labels = this.collectionSummary.data.map(row => moment()
                    .month(row.month - 1)
                    .year(row.year)
                    .format('MMM \'YY'));

            this.chartData.datasets[0].data = this.collectionSummary.data.map(row => row.collected / 100);
            this.chartData.datasets[1].data = this.collectionSummary.data.map(row => (row.invoiced - row.collected) / 100);

            this.legendInvoiceTotal = this.collectionSummary.totals.invoiced;
            this.legendCollectedTotal = this.collectionSummary.totals.collected;
        }
        this.chart?.update();
        this._cd.detectChanges();
    }


    determinePermissions() {
        this.permissions['communication'] = {read: false, write: false};
        this.permissions['communication'].read = LegFiJwtService.doesUserHaveModulePermission('communication', false);
        this.permissions['communication'].write = LegFiJwtService.doesUserHaveModulePermission('communication', true);

        this.hasWriteAccess = LegFiJwtService.doesUserHaveModulePermission('charges', true);
    }


    updateGraphDisplayType(showCalendar: number) {
        this.showCalendarYear = showCalendar;
        this.getCollectionProgressGraph();
    }

    updateGraphYear(year: number) {
        this.graphYear = year;
        this.getCollectionProgressGraph();
    }

    resetChartData() {
        this.drilldown = null;
        this.buildChartData();
    }
}
